import React, { useState } from 'react';
import { DocumentTextIcon, CloudIcon, EyeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import StorageFilesModal from '../components/StorageFilesModal';

// Helper to format bytes to human readable
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to convert GB to bytes
const gbToBytes = (gb) => gb * 1024 * 1024 * 1024;

// Progress bar component
const UsageProgressBar = ({ used, limit, label, icon: Icon, formatUsed, formatLimit, warningThreshold = 70, criticalThreshold = 90, allowOveruse = false }) => {
  const percent = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;

  const getProgressColor = () => {
    if (percent >= criticalThreshold) return 'bg-red-500';
    if (percent >= warningThreshold) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getBackgroundColor = () => {
    if (percent >= criticalThreshold) return 'bg-red-100';
    if (percent >= warningThreshold) return 'bg-yellow-100';
    return 'bg-gray-200';
  };

  return (
    <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-azul-surelia/10 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-azul-surelia" />
        </div>
        <div className="flex items-center gap-2">
          <Heading level={3} className="text-lg font-semibold text-azul-surelia">
            {label}
          </Heading>
          {allowOveruse && (
            <div className="relative group">
              <InformationCircleIcon className="w-4 h-4 text-azul-surelia/50 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                Si excedes el limite, se cobrara el diferencial
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Text className="text-sm text-azul-surelia/70">Usado</Text>
          <Text className="text-sm font-medium text-azul-surelia">
            {formatUsed} / {formatLimit}
          </Text>
        </div>

        <div className={`w-full ${getBackgroundColor()} rounded-full h-3`}>
          <div
            className={`h-3 rounded-full transition-all duration-500 ${getProgressColor()}`}
            style={{ width: `${percent}%` }}
          />
        </div>

        <div className="flex items-center justify-between">
          <Text className="text-xs text-azul-surelia/50">
            {percent.toFixed(1)}% utilizado
          </Text>
          {percent >= criticalThreshold && (
            <Text className="text-xs text-red-600 font-medium">
              Limite casi alcanzado
            </Text>
          )}
          {percent >= warningThreshold && percent < criticalThreshold && (
            <Text className="text-xs text-yellow-600 font-medium">
              Uso elevado
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfiguracionUso = ({
  plan,
  planLoading,
  usage,
  usageLoading,
  storageUsage,
  storageUsageLoading,
  orgData
}) => {
  const [showFilesModal, setShowFilesModal] = useState(false);

  // Pages usage
  const pageLimit = plan?.page_limit || 0;
  const pagesUsed = usage?.total_pages || 0;

  // Storage usage (plan.storage is in GB, storageUsage.total_bytes is in bytes)
  const storageLimitGB = plan?.storage || 0;
  const storageLimitBytes = gbToBytes(storageLimitGB);
  const storageUsedBytes = storageUsage?.total_bytes || 0;
  const fileCount = storageUsage?.file_count || 0;

  // Get current month name
  const currentMonth = new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });

  if (planLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
          <Text className="text-azul-surelia/70">Cargando informacion del plan...</Text>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
          <Text className="text-azul-surelia/70">No hay informacion de plan disponible.</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <Heading level={2} className="text-xl font-bold text-azul-surelia">
              Uso de Recursos
            </Heading>
            <Text className="text-azul-surelia/70 mt-1">
              Plan {plan.name} - {orgData?.nombre || 'Tu organizacion'}
            </Text>
          </div>
          <div className="text-right">
            <Text className="text-sm text-azul-surelia/50">Periodo actual</Text>
            <Text className="text-sm font-medium text-azul-surelia capitalize">{currentMonth}</Text>
          </div>
        </div>
      </div>

      {/* Usage Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pages Usage */}
        {usageLoading ? (
          <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
            <Text className="text-azul-surelia/70">Cargando uso de paginas...</Text>
          </div>
        ) : (
          <UsageProgressBar
            used={pagesUsed}
            limit={pageLimit}
            label="Paginas Procesadas"
            icon={DocumentTextIcon}
            formatUsed={pagesUsed.toLocaleString()}
            formatLimit={pageLimit.toLocaleString()}
            allowOveruse={!plan.limit_enforced}
          />
        )}

        {/* Storage Usage */}
        {storageUsageLoading ? (
          <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
            <Text className="text-azul-surelia/70">Cargando uso de almacenamiento...</Text>
          </div>
        ) : (
          <UsageProgressBar
            used={storageUsedBytes}
            limit={storageLimitBytes}
            label="Almacenamiento"
            icon={CloudIcon}
            formatUsed={formatBytes(storageUsedBytes)}
            formatLimit={`${storageLimitGB} GB`}
            allowOveruse={!plan.limit_enforced}
          />
        )}
      </div>

      {/* Additional Stats */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
        <Heading level={3} className="text-lg font-semibold text-azul-surelia mb-4">
          Detalles Adicionales
        </Heading>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-azul-surelia/5 rounded-lg p-4">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-2">Paginas Disponibles</Text>
            <Text className="text-xl font-bold text-azul-surelia">
              {Math.max(0, pageLimit - pagesUsed).toLocaleString()}
            </Text>
          </div>
          <div className="bg-azul-surelia/5 rounded-lg p-4">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-2">Almacenamiento Disponible</Text>
            <Text className="text-xl font-bold text-azul-surelia">
              {formatBytes(Math.max(0, storageLimitBytes - storageUsedBytes))}
            </Text>
          </div>
          <div className="bg-azul-surelia/5 rounded-lg p-4">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-2">Archivos Almacenados</Text>
            <div className="flex items-center justify-between">
              <Text className="text-xl font-bold text-azul-surelia">
                {fileCount.toLocaleString()}
              </Text>
              <button
                onClick={() => setShowFilesModal(true)}
                className="flex items-center gap-1 text-xs text-azul-surelia hover:text-azul-surelia/80 hover:underline"
              >
                <EyeIcon className="w-3.5 h-3.5" />
                Ver detalle
              </button>
            </div>
          </div>
          <div className="bg-azul-surelia/5 rounded-lg p-4">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-2">Costo por Pagina Extra</Text>
            <Text className="text-xl font-bold text-azul-surelia">
              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(plan.extra_page_price || 0)}
            </Text>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <Text className="text-sm text-blue-800">
          <strong>Nota:</strong> El uso de paginas se reinicia cada mes. El almacenamiento es acumulativo.
          Si excedes los limites de tu plan, se cobrara el diferencial segun las tarifas establecidas.
        </Text>
      </div>

      {/* Storage Files Modal */}
      <StorageFilesModal
        isOpen={showFilesModal}
        onClose={() => setShowFilesModal(false)}
        orgId={orgData?.id}
      />
    </div>
  );
};

export default ConfiguracionUso;
